import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ProductCard from "@/components/ProductCard";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";

const getPoStatusRank = (product) => Number(product?.is_po_open_calculated ?? product?.is_po_open ?? 1) === 1 ? 0 : 1;

const getPoCloseTime = (product) => {
  if (!product?.preorder_closing_date) return Number.NEGATIVE_INFINITY;
  const time = new Date(product.preorder_closing_date).getTime();
  return Number.isFinite(time) ? time : Number.NEGATIVE_INFINITY;
};

const stableSort = (items = [], compare) => {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => compare(a.item, b.item) || a.index - b.index)
    .map(({ item }) => item);
};

const sortProductsByClosePo = (items = []) => {
  return stableSort(items, (a, b) => {
    const aStatusRank = getPoStatusRank(a);
    const bStatusRank = getPoStatusRank(b);

    if (aStatusRank !== bStatusRank) return aStatusRank - bStatusRank;

    const aDate = getPoCloseTime(a);
    const bDate = getPoCloseTime(b);

    if (aDate !== bDate) return bDate - aDate;
    return String(a?.name || "").localeCompare(String(b?.name || ""));
  });
};

const sortProductsForListing = (items = []) => {
  const nonPreorderItems = items.filter(item => item?.stock_type !== "preorder");
  const openPreorderItems = sortProductsByClosePo(
    items.filter(item => item?.stock_type === "preorder" && getPoStatusRank(item) === 0)
  );
  const closedPreorderItems = sortProductsByClosePo(
    items.filter(item => item?.stock_type === "preorder" && getPoStatusRank(item) !== 0)
  );

  return [...nonPreorderItems, ...openPreorderItems, ...closedPreorderItems];
};

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [splitTotals, setSplitTotals] = useState({ preorder: 0, ready: 0 });
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [wishlistIds, setWishlistIds] = useState([]);
  const { user } = useAuth();
  const { t, locale } = useLanguage();
  const isReseller = ["reseller", "agen", "mitra", "produsen", "distributor", "admin"].includes(user?.role);
  const stockType = searchParams.get("stock_type") || "";
  const searchQuery = searchParams.get("search") || "";
  const currentPage = Math.max(1, Number(searchParams.get("page") || 1));
  const itemsPerPage = 20;
  const showSplitCatalog = !stockType;

  useEffect(() => {
    if (user) API.get("/wishlist/ids").then((r) => setWishlistIds(r.data.product_ids || [])).catch(() => {});
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const hasProducts = products.length > 0;

    if (hasProducts) setIsFetching(true);
    else setLoading(true);

    if (showSplitCatalog) {
      const search = params.get("search") || "";
      const preorderParams = new URLSearchParams({ stock_type: "preorder", limit: "20", summary: "true" });
      const readyParams = new URLSearchParams({ stock_type: "ready_stock", limit: "20", summary: "true" });
      if (search.trim()) {
        preorderParams.set("search", search.trim());
        readyParams.set("search", search.trim());
      }
      Promise.all([
        API.get(`/products?${preorderParams.toString()}`),
        API.get(`/products?${readyParams.toString()}`),
      ]).then(([preorderRes, readyRes]) => {
        const preorderProducts = sortProductsByClosePo(preorderRes.data.products || []);
        const readyProducts = sortProductsForListing(readyRes.data.products || []);
        setProducts([...preorderProducts, ...readyProducts]);
        setSplitTotals({ preorder: Number(preorderRes.data.total || 0), ready: Number(readyRes.data.total || 0) });
        setTotal(Number(preorderRes.data.total || 0) + Number(readyRes.data.total || 0));
      }).catch(() => {}).finally(() => {
        setLoading(false);
        setIsFetching(false);
      });
      return;
    }

    params.set("limit", String(itemsPerPage));
    params.set("skip", String((currentPage - 1) * itemsPerPage));
    params.set("summary", "true");
    params.delete("page");

    API.get(`/products?${params.toString()}`).then((r) => {
      const fetchedProducts = r.data.products || [];
      const isPreorderPage = params.get("stock_type") === "preorder";
      setProducts(isPreorderPage ? sortProductsByClosePo(fetchedProducts) : sortProductsForListing(fetchedProducts));
      setTotal(Number(r.data.total || 0));
    }).catch(() => {}).finally(() => {
      setLoading(false);
      setIsFetching(false);
    });
  }, [searchParams, currentPage, showSplitCatalog]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  const readyProducts = products.filter((item) => item?.stock_type !== "preorder");
  const preorderProducts = sortProductsByClosePo(products.filter((item) => item?.stock_type === "preorder"));
  const showPreorderSeeMore = splitTotals.preorder > preorderProducts.length;
  const showReadySeeMore = splitTotals.ready > readyProducts.length;

  const updateParams = (updater) => {
    const params = new URLSearchParams(searchParams);
    updater(params);
    setSearchParams(params);
  };

  const handleSearchChange = (value) => {
    updateParams((params) => {
      if (value.trim()) params.set("search", value);
      else params.delete("search");
      params.delete("page");
    });
  };

  const handlePageChange = (page) => {
    updateParams((params) => {
      if (page > 1) params.set("page", String(page));
      else params.delete("page");
    });
  };

  const renderProductsGrid = (items, testId = "products-grid") => (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-8 sm:gap-x-6 sm:gap-y-12" data-testid={testId}>
      {items.map((p) => (
        <ProductCard key={p.id} product={p} showReseller={isReseller} wishlistIds={wishlistIds} onWishlistChange={() => API.get("/wishlist/ids").then((r) => setWishlistIds(r.data.product_ids || []))} />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen" data-testid="products-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="mb-8">
          <div className="relative max-w-xl" data-testid="products-title">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t("common.search_products") || (locale === "id" ? "Cari produk..." : "Search products...")}
              className="w-full rounded-full border border-stone-200 bg-white py-3 pl-11 pr-4 text-sm text-stone-800 shadow-sm outline-none transition-all placeholder:text-stone-400 focus:border-stone-400 focus:ring-2 focus:ring-stone-100"
            />
          </div>
          <p className="mt-3 text-sm text-stone-500">{total} {t("common.products_found")}</p>
        </div>

        {loading && products.length === 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-8 sm:gap-x-6 sm:gap-y-12">
            {[1,2,3,4].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-stone-200 mb-4" />
                <div className="h-3 bg-stone-200 w-2/3 mb-2" />
                <div className="h-4 bg-stone-200 w-full mb-2" />
                <div className="h-3 bg-stone-200 w-1/3" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-stone-500">{t("common.no_products")}</p>
          </div>
        ) : (
          <div className="relative">
            {isFetching && products.length > 0 && (
              <div className="absolute top-0 right-0 z-10 rounded-full border border-stone-200 bg-white/90 px-3 py-1 text-xs text-stone-500 shadow-sm backdrop-blur-sm">
                {t("common.loading")}
              </div>
            )}

            {showSplitCatalog ? (
              <div className="space-y-16">
                <section>
                  <div className="mb-6">
                    <h2 className="font-serif text-2xl sm:text-3xl text-stone-900">
                      {locale === "id" ? "Katalog PO" : "Preorder Catalog"}
                    </h2>
                    <p className="text-sm text-stone-500 mt-1">{splitTotals.preorder} {t("common.products_found")}</p>
                  </div>
                  {preorderProducts.length > 0 ? (
                    <>
                      {renderProductsGrid(preorderProducts, "products-grid-preorder")}
                      {showPreorderSeeMore && (
                        <div className="mt-8 flex justify-center">
                          <Link
                            to="/products?stock_type=preorder"
                            className="group inline-flex items-center justify-center gap-2 rounded-full bg-stone-900 px-6 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-stone-800"
                          >
                            <span>{locale === "id" ? "Lihat lebih banyak" : "See more"}</span>
                            <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                          </Link>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-10 border border-stone-100 rounded-xl text-stone-500 bg-stone-50/50">
                      {locale === "id" ? "Belum ada produk preorder." : "No preorder products yet."}
                    </div>
                  )}
                </section>

                <section>
                  <div className="mb-6">
                    <h2 className="font-serif text-2xl sm:text-3xl text-stone-900">
                      {locale === "id" ? "Katalog Ready" : "Ready Stock Catalog"}
                    </h2>
                    <p className="text-sm text-stone-500 mt-1">{splitTotals.ready} {t("common.products_found")}</p>
                  </div>
                  {readyProducts.length > 0 ? (
                    <>
                      {renderProductsGrid(readyProducts, "products-grid-ready")}
                      {showReadySeeMore && (
                        <div className="mt-8 flex justify-center">
                          <Link
                            to="/products?stock_type=ready_stock"
                            className="group inline-flex items-center justify-center gap-2 rounded-full bg-stone-900 px-6 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-stone-800"
                          >
                            <span>{locale === "id" ? "Lihat lebih banyak" : "See more"}</span>
                            <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                          </Link>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-10 border border-stone-100 rounded-xl text-stone-500 bg-stone-50/50">
                      {locale === "id" ? "Belum ada produk ready stock." : "No ready stock products yet."}
                    </div>
                  )}
                </section>
              </div>
            ) : (
              <>
                {renderProductsGrid(products)}

                {totalPages > 1 && (
                  <div className="flex items-center justify-between border-t border-stone-100 pt-6 mt-12">
                    <p className="text-sm text-stone-500">{t("common.page")} {currentPage} {t("common.of")} {totalPages}</p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="p-2 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50 transition-colors"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="p-2 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50 transition-colors"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
